// «Шаблон по тренировке»: перенести подходы и упражнения завершённой тренировки в её шаблон
// (или сохранить тренировку без шаблона как новый). Перед применением — список изменений.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge, Button, Card, ErrorText, Input, Modal, Spinner, useToast, type BadgeTone } from '@/components/ui'

import { useApplySessionTemplate, useSessionTemplatePreview, type Session, type TemplateChange } from './api'

const KIND: Record<TemplateChange['kind'], { label: string; tone: BadgeTone }> = {
  added: { label: 'добавится', tone: 'accent' },
  changed: { label: 'изменится', tone: 'warning' },
  progressed: { label: 'прогрессия', tone: 'accent' },
  removed: { label: 'уйдёт', tone: 'danger' },
}

function ChangeLine({ change }: { change: TemplateChange }) {
  const kind = KIND[change.kind]
  return (
    <li className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 text-sm font-medium text-fg">{change.exercise_name}</span>
        <Badge tone={kind.tone}>{kind.label}</Badge>
      </div>
      <div className="text-sm tabular-nums text-muted">
        {change.before && change.after ? (
          <>
            {change.before} → <span className="text-fg">{change.after}</span>
          </>
        ) : (
          (change.after ?? change.before)
        )}
      </div>
    </li>
  )
}

export function TemplateSyncCard({ session }: { session: Session }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState<string | null>(null)
  const preview = useSessionTemplatePreview(session.id, open)
  const apply = useApplySessionTemplate(session.id)
  const toast = useToast()
  const navigate = useNavigate()

  const create = !session.template_id
  const data = preview.data
  const nothing = data?.mode === 'update' && data.changes.length === 0 && !data.reordered
  const close = () => {
    setOpen(false)
    setName(null)
    apply.reset()
  }

  return (
    <>
      <Card className="mt-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <p className="min-w-0 flex-[1_1_14rem] text-sm text-muted">
            {create
              ? 'Понравилась тренировка? Сохрани её как шаблон — упражнения и подходы станут целями.'
              : `Делал не как в шаблоне «${session.template_name ?? ''}»? Перенеси упражнения и подходы в шаблон.`}
          </p>
          <Button icon={create ? 'plus' : 'list'} onClick={() => setOpen(true)}>
            {create ? 'Сохранить как шаблон' : 'Обновить шаблон'}
          </Button>
        </div>
      </Card>
      <Modal
        open={open}
        onClose={close}
        title={create ? 'Новый шаблон из тренировки' : `Обновить «${session.template_name ?? 'шаблон'}»`}
        description="Шаблон станет таким, как эта тренировка: упражнения, порядок и рабочие подходы, без разминки. Отменить можно в журнале изменений."
        footer={
          <>
            <Button onClick={close}>{nothing ? 'Закрыть' : 'Отмена'}</Button>
            {data && !nothing ? (
              <Button
                variant="primary"
                loading={apply.isPending}
                onClick={() =>
                  apply.mutate(create ? (name ?? data.suggested_name) : undefined, {
                    onSuccess: (template) => {
                      close()
                      toast.show({
                        message: create ? `Шаблон «${template.name}» создан` : `Шаблон «${template.name}» обновлён`,
                        tone: 'accent',
                        action: { label: 'Открыть', onClick: () => navigate(`/templates/${template.id}`) },
                      })
                    },
                  })
                }
              >
                {create ? 'Создать' : 'Обновить'}
              </Button>
            ) : null}
          </>
        }
      >
        {preview.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : preview.error ? (
          <ErrorText error={preview.error} />
        ) : data ? (
          <div className="flex flex-col gap-3">
            {create ? (
              <Input
                label="Название"
                value={name ?? data.suggested_name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
              />
            ) : null}
            {nothing ? (
              <p className="text-sm text-fg">Шаблон уже совпадает с тренировкой — менять нечего.</p>
            ) : (
              <ul>
                {data.changes.map((c, i) => (
                  <ChangeLine key={`${c.kind}:${c.exercise_name}:${i}`} change={c} />
                ))}
              </ul>
            )}
            {data.reordered ? <p className="text-sm text-muted">Порядок упражнений — как в тренировке.</p> : null}
            {data.unchanged > 0 && !nothing ? (
              <p className="text-sm text-muted">Без изменений: {data.unchanged}.</p>
            ) : null}
            {data.changes.some((c) => c.kind === 'progressed') ? (
              <p className="text-sm text-muted">«Прогрессия» — сделано по плану, в шаблон запишется вес следующей тренировки.</p>
            ) : null}
            <ErrorText error={apply.error} />
          </div>
        ) : null}
      </Modal>
    </>
  )
}
